// Advance-credit application helper.
//
// When a new maintenance/rent due is created for a tenant who already has
// advance credit on file (see `pay.advance` on older payment records), we
// auto-consume that advance FIFO and mark the new due paid/partial.
//
// Advance storage model (pre-existing):
//   A payment record is considered an advance source when
//   `p.st === "advance"` OR `(p.advance || 0) > 0`.
//   The numeric balance lives on `p.advance` (₹).
//
// This util is deliberately pure: it returns the new/updated records but does
// NOT persist or mutate React state. The caller is responsible for:
//   • calling `api.savePayment(...)` on the returned `newPay` and each entry
//     of `updatedPays`
//   • merging `updatedPays` + `newPay` into the `pays` state
//
// Callers creating several dues in a batch (e.g. one maintenance charge
// split across multiple tenants) should thread a running `pays` array
// through successive calls so advance consumed on one iteration is not
// re-offered to the next.

/**
 * Consume tenant advance credit to cover a freshly-created due payment.
 *
 * @param {Object} newPay - The new due record. Expected shape:
 *   { id, tid, amt, com, mo, st: "due", dt, advance, txns }
 * @param {Array}  pays   - Current payment records (scanned for advance).
 * @param {string} tid    - Tenant id (must equal `newPay.tid`).
 * @returns {{ newPay: Object, updatedPays: Object[], consumed: number }}
 *   newPay:      the target record, possibly upgraded to st: "paid" / "partial"
 *                with an auto-generated "advance-applied" verified txn.
 *   updatedPays: advance-source records whose `advance` field was decremented.
 *   consumed:    total ₹ of advance applied (0 if none available).
 */
export function applyAdvanceToDue(newPay, pays, tid) {
  const dueAmt = (newPay.amt || 0) + (newPay.com || 0);
  if (dueAmt <= 0) return { newPay, updatedPays: [], consumed: 0 };

  // Advance-bearing records for this tenant, oldest first (FIFO).
  // Using dt then id as tiebreaker so ordering is stable across reloads.
  const sources = pays
    .filter(p => p.tid === tid && (p.advance || 0) > 0)
    .sort((a, b) => (a.dt || "").localeCompare(b.dt || "") || (a.id || "").localeCompare(b.id || ""));

  if (sources.length === 0) return { newPay, updatedPays: [], consumed: 0 };

  let remaining = dueAmt;
  let consumed = 0;
  const updatedPays = [];

  for (const src of sources) {
    if (remaining <= 0) break;
    const take = Math.min(src.advance || 0, remaining);
    if (take <= 0) continue;
    updatedPays.push({ ...src, advance: (src.advance || 0) - take });
    remaining -= take;
    consumed += take;
  }

  if (consumed <= 0) return { newPay, updatedPays: [], consumed: 0 };

  const now = new Date();
  const dtStr = now.toISOString().slice(0, 10);
  const advTxn = {
    id: `tx_adv_${Date.now()}`,
    tid,
    amt: consumed,
    mth: "advance",
    dt: dtStr,
    type: "advance-applied",
    status: "verified",
    mo: newPay.mo,
    totalWas: dueAmt,
    note: "Auto-applied from tenant advance credit",
  };

  const fullyCovered = consumed >= dueAmt;
  const updatedNewPay = {
    ...newPay,
    st: fullyCovered ? "paid" : "partial",
    dt: dtStr,
    txns: [...(newPay.txns || []), advTxn],
  };

  return { newPay: updatedNewPay, updatedPays, consumed };
}
